import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

// components
import AppCard from 'src/components/AppCard'
import AppCardHeader from 'src/components/card/AppCardHeader'
import AppDetails from '../../components/details/setting-details/AppDetails'
import AppTable from 'src/components/AppTable'

// helpers
import Axios from '../../helpers/axios'
import { columnLetterNumberList, columnCompanyBankAccountList } from '../../components/table-column/TableColumnList'
import { formUrlQuery } from 'src/helpers/format-queries'
import AppModalConfirmation from 'src/components/modal/AppModalConfirmation'
import useToastMessage from 'src/helpers/toast-message'

const SettingDetails = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { showSuccess, showError } = useToastMessage();

  const generalSettings = JSON.parse(localStorage.getItem("general_setting"))

  const [showConfirmView, setShowConfirmView] = useState({ show: false, item: {} })

  const [generalSettingDetail, setGeneralSettingDetail] = useState(null)

  const [letterNumberListCount, setLetterNumberListCount] = useState(0)
  const [letterNumberListPerPage, setLetterNumberListPerPage] = useState(10);
  const [activeLetterNumberListPage, setActiveLetterNumberListPage] = useState(1);
  const [searchLetterNumberListFilter, setSearchLetterNumberListFilter] = useState("");
  const [columnLetterNumberListSorter, setColumnLetterNumberListSorter] = useState({ column: "createdAt", state: "DESC" });
  const [letterNumberList, setLetterNumberList] = useState([])

  const [companyBankAccountListCount, setCompanyBankAccountListCount] = useState(0)
  const [companyBankAccountListPerPage, setCompanyBankAccountListPerPage] = useState(10);
  const [activeCompanyBankAccountListPage, setActiveCompanyBankAccountListPage] = useState(1);
  const [searchCompanyBankAccountListFilter, setSearchCompanyBankAccountListFilter] = useState("");
  const [columnCompanyBankAccountListSorter, setColumnCompanyBankAccountListSorter] = useState({ column: "createdAt", state: "DESC" });
  const [companyBankAccountList, setCompanyBankAccountList] = useState([])

  const handleCardHeaderButton = () => {
    if (generalSettingDetail?.id) {
      navigate(`/generalsetting/edit/${generalSettingDetail?.id}`)
    } else {
      navigate(`/generalsetting/add`)
    }
  }

  useEffect(() => {
    if (generalSettings?.id) {
      setGeneralSettingDetail(generalSettings)
    } else {
      fetchDetailGeneralSetting()
    }
  }, [])

  useEffect(() => {
    let queries = handleQueriesLetterNumber()
    fetchAllLetterNumber(queries)
  }, [
    searchLetterNumberListFilter,
    letterNumberListPerPage,
    activeLetterNumberListPage,
    columnLetterNumberListSorter
  ])

  const handleQueriesLetterNumber = () => {
    let queries = {
      search: searchLetterNumberListFilter,
      limit: letterNumberListPerPage,
      offset: letterNumberListPerPage * activeLetterNumberListPage - letterNumberListPerPage,
      sort:
        (columnLetterNumberListSorter &&
          `${columnLetterNumberListSorter.column}%${columnLetterNumberListSorter.state}`) ||
        "",
    };
    if (generalSettingDetail?.id) {
      queries.general_setting_id = generalSettingDetail?.id
    }
    queries = formUrlQuery(queries);

    return queries
  }

  useEffect(() => {
    let queries = handleQueriesCompanyBankAccount()
    fetchAllCompanyBankAccount(queries)
  }, [
    searchCompanyBankAccountListFilter,
    companyBankAccountListPerPage,
    activeCompanyBankAccountListPage,
    columnCompanyBankAccountListSorter
  ])

  const handleQueriesCompanyBankAccount = () => {
    let queries = {
      search: searchCompanyBankAccountListFilter,
      limit: companyBankAccountListPerPage,
      offset: companyBankAccountListPerPage * activeCompanyBankAccountListPage - companyBankAccountListPerPage,
      sort:
        (columnCompanyBankAccountListSorter &&
          `${columnCompanyBankAccountListSorter.column}%${columnCompanyBankAccountListSorter.state}`) ||
        "",
    };
    queries = formUrlQuery(queries);

    return queries
  }

  const fetchDetailGeneralSetting = () => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })

    Axios({
      method: "get",
      url: `/api/tracker/v1/generalsetting`,
    })
      .then(({ data }) => {
        if (data?.success) {
          const tempGeneralSettingDetail = data?.value
          setGeneralSettingDetail(tempGeneralSettingDetail)

          if (!generalSettings) {
            localStorage.setItem("general_setting", JSON.stringify(tempGeneralSettingDetail))
          }
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const fetchAllLetterNumber = (queries) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })

    Axios({
      method: "get",
      url: `/api/tracker/v1/generalsetting/letternumber/list?${queries}`,
    })
      .then(({ data }) => {
        if (data?.success) {
          const tempLetterNumberList = data?.value
          setLetterNumberList(tempLetterNumberList)
          if (activeLetterNumberListPage === 1) {
            setLetterNumberListCount(data?.count)
          }
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const fetchAllCompanyBankAccount = (queries) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })

    Axios({
      method: "get",
      url: `/api/tracker/v1/generalsetting/companybankaccount/list?${queries}`,
    })
      .then(({ data }) => {
        if (data?.success) {
          const tempCompanyBankAccountList = data?.value
          setCompanyBankAccountList(tempCompanyBankAccountList)
          if (activeCompanyBankAccountListPage === 1) {
            setCompanyBankAccountListCount(data?.count)
          }
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleSearchItemsLetterNumber = (search) => {
    setActiveLetterNumberListPage(1)
    setSearchLetterNumberListFilter(search);
  }

  const handleSearchItemsCompanyBankAccount = (search) => {
    setActiveCompanyBankAccountListPage(1)
    setSearchCompanyBankAccountListFilter(search);
  }

  const copyToClipboard = async (val) => {
    try {
      await navigator.clipboard.writeText(val);
      showSuccess('Letter number has been copied to your clipboard')
    } catch (err) {
      showError('Failed to copy!')
    }
  };

  const handleOnRowLetterNumberClick = (item, columnName) => {
    if (columnName !== 'action') {
      setShowConfirmView({ show: true, item, type: 'letternumber' })
    }
  }

  const handleOnRowCompanyBankAccountClick = (item, columnName) => {
    if (columnName !== 'action') {
      setShowConfirmView({ show: true, item, type: 'companybankaccount' })
    }
  }

  return (
    <>
      {
        showConfirmView.show && (
          <AppModalConfirmation 
            confirmationType={showConfirmView.type === 'letternumber' ? 'detail-letter-number' : 'detail-company-bank-account'}
            itemName={showConfirmView.type === 'letternumber' ? 'detail-letter-number' : 'detail-company-bank-account'}
            itemDetails={showConfirmView.item}
            itemType={showConfirmView.type}
            showModal={showConfirmView}
            setShowModal={setShowConfirmView}
          />
        )
      }
      <AppCard
        className='mt-4 mb-4'
        headerTitle={
          <AppCardHeader
            headerTitle={<h4>General Setting</h4>}
            headerButton={generalSettingDetail?.id ? "Edit" : "Create"}
            handleCardHeaderButton={handleCardHeaderButton}
          />
        }
        bodyContent={
          <AppDetails
            settingDetails={generalSettingDetail}
          />
        }
        lowerBodyContent={
          <>
            <AppCard 
              className='mb-4'
              headerTitle={
                <AppCardHeader
                  headerTitle={<h4>Letter Number</h4>}
                />
              }
              bodyContent={
                <AppTable
                  letterNumberListPage
                  tableFilter
                  tableColumns={columnLetterNumberList}
                  tableContent={letterNumberList}
                  contentPerPage={letterNumberListPerPage}
                  activeContentPage={activeLetterNumberListPage}
                  contentCount={letterNumberListCount}
                  searchContentFilter={searchLetterNumberListFilter}
                  isShowModalEdit={false}
                  isShowDropdownMenu={false}
                  isShowButtonCopy={true}
                  handleCopyToClipboard={copyToClipboard}
                  setSearchContentFilter={handleSearchItemsLetterNumber}
                  setActiveContentPage={setActiveLetterNumberListPage}
                  setContentPerPage={setLetterNumberListPerPage}
                  setColumnContentSorter={setColumnLetterNumberListSorter}
                  handleOnRowClick={handleOnRowLetterNumberClick}
                  isClickable={true}
                />
              }
            />
            <AppCard 
              className='mb-4'
              headerTitle={
                <AppCardHeader
                  headerTitle={<h4>Company Bank Account</h4>}
                />
              }
              bodyContent={
                <AppTable
                  companyBankAccountListPage
                  tableFilter
                  tableColumns={columnCompanyBankAccountList}
                  tableContent={companyBankAccountList}
                  contentPerPage={companyBankAccountListPerPage}
                  activeContentPage={activeCompanyBankAccountListPage}
                  contentCount={companyBankAccountListCount}
                  searchContentFilter={searchCompanyBankAccountListFilter}
                  isShowModalEdit={false}
                  isShowDropdownMenu={false}
                  setSearchContentFilter={handleSearchItemsCompanyBankAccount}
                  setActiveContentPage={setActiveCompanyBankAccountListPage}
                  setContentPerPage={setCompanyBankAccountListPerPage}
                  setColumnContentSorter={setColumnCompanyBankAccountListSorter}
                  handleOnRowClick={handleOnRowCompanyBankAccountClick}
                  isClickable={true}
                />
              }
            />
          </>
        }
      />
    </>
  )
}

export default SettingDetails
